import React, { useState, useEffect, useRef } from "react";
import Hls from "hls.js";
import io from "socket.io-client";
import { Send, Settings, Radio, MonitorPlay, Video as VideoIcon, Youtube } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useStreaming } from "../../lib/StreamingContext";
import "../../styles/NonoChannelLive.css";

// --- SUB-COMPONENTS (For cleaner code) ---

// 1. Chat Message Component
const ChatMessage = ({ msg }) => {
  const isSystem = msg.type === 'system';
  const username = msg.username || "Anonyme";
  
  return (
    <div className={`chat-message ${isSystem ? 'system-msg' : ''}`}>
      {!isSystem && <span className="chat-user">{username}:</span>}
      <span className="chat-text">{msg.text || msg.message}</span>
    </div>
  );
};

// 2. Control Panel Component (Admin Only)
const AdminControls = ({ mode, setMode, onStartObs, liveCreated, youtubeUrl, setYoutubeUrl }) => {
  return (
    <div className="admin-controls">
      <h3><Settings size={18} /> Configuration du Direct</h3>
      
      <div className="mode-selector">
        <button 
          className={mode === 'obs' ? 'active' : ''} 
          onClick={() => setMode('obs')}
        >
          <Radio size={16} /> OBS / RTMP
        </button>
        <button 
          className={mode === 'youtube' ? 'active' : ''} 
          onClick={() => setMode('youtube')}
        >
          <Youtube size={16} /> YouTube Live
        </button>
        <button 
          className={mode === 'webcam' ? 'active' : ''} 
          onClick={() => setMode('webcam')}
        >
          <VideoIcon size={16} /> Webcam
        </button>
      </div>

      <div className="control-actions">
        {mode === 'obs' && !liveCreated && (
          <button className="btn-primary" onClick={onStartObs}>
            Générer Clé de Stream OBS
          </button>
        )}
        
        {mode === 'obs' && liveCreated && (
          <div className="stream-key-display">
            <p>Serveur: <code>rtmp://api.nms06.com/live</code></p>
            <p>Clé: <code>stream</code></p>
            <span className="status-badge">Prêt à diffuser</span>
          </div>
        )}

        {mode === 'youtube' && (
          <div className="youtube-input">
            <input 
              type="text" 
              value={youtubeUrl} 
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="ID Vidéo ou URL Embed"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---

const NonoChannelLive = () => {
  const { user, isAuthenticated } = useAuth();
  const { streamUrl, setStreamUrl } = useStreaming(); // Context for global stream state
  
  // Refs
  const videoRef = useRef(null);
  const chatEndRef = useRef(null);
  const socketRef = useRef(null);

  // State
  const [mode, setMode] = useState("obs"); // 'obs', 'youtube', 'webcam'
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [liveCreated, setLiveCreated] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("https://www.youtube.com/embed/live_stream?channel=YOUR_CHANNEL_ID");
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  // 1. Socket.io Connection
  useEffect(() => {
    socketRef.current = io("https://api.nms06.com/", {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Socket connected");
      // Optional: Join specific room
      // socketRef.current.emit('join_room', 'live_stream');
    });

    socketRef.current.on("message", (payload) => {
      setChat((prev) => [...prev, payload]);
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  // 2. Chat Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  // 3. Handle OBS/HLS Stream Playback
  useEffect(() => {
    if (mode !== 'obs' && mode !== 'webcam') return;
    if (!streamUrl || !videoRef.current) return;

    let hls;
    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(streamUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoRef.current.play().catch(e => console.log("Auto-play prevented", e));
      });
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
      videoRef.current.src = streamUrl;
      videoRef.current.play();
    }

    return () => {
      if (hls) hls.destroy();
    };
  }, [streamUrl, mode]);

  // 4. Actions
  const sendMessage = (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const payload = {
      username: user ? `${user.first_name}` : "Invité",
      message: message.trim(),
      timestamp: new Date().toISOString()
    };

    socketRef.current.emit("message", payload);
    setMessage("");
  };

  const handleCreateObsLive = async () => {
    // Logic to notify backend to expect an OBS stream
    // For now, we just set the URL to the standard HLS endpoint
    const hlsUrl = `https://api.nms06.com/hls/stream.m3u8`;
    setStreamUrl(hlsUrl);
    setLiveCreated(true);
    
    // Optional: Call API to update status in DB
    /* await fetch("https://api.nms06.com/api/live-stream", { 
       method: "POST", 
       body: JSON.stringify({ status: 'active', type: 'obs' }) 
    });
    */
  };

  // Webcam Logic (Simplified for this example)
  const handleWebcamStart = async () => {
     try {
       const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
       if(videoRef.current) videoRef.current.srcObject = stream;
       // Note: To stream this to others, you need WebRTC or send blobs to server.
       // Current implementation only shows local mirror.
     } catch(err) {
       console.error("Webcam error", err);
     }
  };

  return (
    <div className="nono-live-page">
      
      {/* Header / Title */}
      <header className="live-header">
        <h1>Nono Channel <span className="live-badge">LIVE</span></h1>
        {isAuthenticated && user.role === 'admin' && (
           <AdminControls 
             mode={mode} 
             setMode={setMode} 
             onStartObs={handleCreateObsLive}
             liveCreated={liveCreated}
             youtubeUrl={youtubeUrl}
             setYoutubeUrl={setYoutubeUrl}
           />
        )}
      </header>

      <div className="live-grid">
        
        {/* LEFT: Video Player */}
        <div className="video-container">
          {mode === 'youtube' ? (
            <iframe
              className="responsive-iframe"
              src={youtubeUrl}
              title="YouTube Live"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          ) : (
            <div className="html5-player-wrapper">
              <video 
                ref={videoRef} 
                controls 
                autoPlay 
                playsInline
                className="html5-video" 
              />
              {!streamUrl && mode === 'obs' && (
                <div className="offline-placeholder">
                  <MonitorPlay size={48} />
                  <p>En attente du flux vidéo...</p>
                </div>
              )}
              {mode === 'webcam' && (
                 <button className="btn-overlay" onClick={handleWebcamStart}>Activer Caméra</button>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Chat */}
        <div className="chat-container">
          <div className="chat-header">Chat en direct</div>
          
          <div className="chat-messages">
            {chat.length === 0 ? (
              <p className="empty-chat">Soyez le premier à dire bonjour ! 👋</p>
            ) : (
              chat.map((msg, i) => <ChatMessage key={i} msg={msg} />)
            )}
            <div ref={chatEndRef} />
          </div>

          <form className="chat-input-form" onSubmit={sendMessage}>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isAuthenticated ? "Participez au chat..." : "Connectez-vous pour parler"}
              disabled={!isAuthenticated}
            />
            <button type="submit" disabled={!isAuthenticated || !message.trim()}>
              <Send size={18} />
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default NonoChannelLive;